/**
 * Journey question helpers (pure — no storage).
 * Compose the questions for one form from the API tracker payload
 * (GET /api/member_tracker/:id). Public join lives on bms-web.
 */

export const FORM_QUESTION_SOURCES = {
  LIBRARY: "library",
  TRACKER: "tracker",
  SHARED: "shared",
  PERSONAL_INFO: "personal_info",
};

export const PERSONAL_INFO_LIBRARY_IDS = [
  "q_first_name",
  "q_last_name",
  "q_email",
  "q_phone",
];

export const isPersonalInfoLibraryId = (questionId) =>
  PERSONAL_INFO_LIBRARY_IDS.includes(questionId);

export const isLeadCaptureTracker = (tracker) =>
  tracker?.category === "lead_capture";

const isFirstSubmissionForm = (form, allForms = []) => {
  if (!form) return false;
  if (form.role === "first_submission") return true;
  if (!allForms.length) return false;
  const designated = allForms.find((f) => f.role === "first_submission");
  if (designated) return designated.id === form.id;
  return allForms[0]?.id === form.id;
};

/** True for measurable / measure-type questions (not personal / general). */
export const isMeasurableQuestion = (q) =>
  Boolean(
    q &&
      (q.measurable === true ||
        String(q.mode || "").toLowerCase() === "measurable" ||
        String(q.measureType || "").trim())
  );

/**
 * Form composition order:
 * 1) Basic info (first submit only)
 * 2) General shared + schedule/form extras
 * 3) Measurable shared + schedule/form extras
 */
export const getEffectiveFormQuestions = (
  form,
  sharedQuestions = [],
  personalInfoQuestions = [],
  allForms = []
) => {
  const excluded = new Set(form?.excludedSharedQuestionIds || []);
  const overrides = form?.sharedOverrides || {};

  const personal = isFirstSubmissionForm(form, allForms)
    ? (personalInfoQuestions || []).map((item) => {
        const override = overrides[item.id] || {};
        return {
          ...item,
          ...override,
          required: true,
          source: FORM_QUESTION_SOURCES.PERSONAL_INFO,
        };
      })
    : [];

  const shared = (sharedQuestions || [])
    .filter(
      (item) =>
        item &&
        !excluded.has(item.id) &&
        !isPersonalInfoLibraryId(item.questionId)
    )
    .map((item) => {
      const override = overrides[item.id] || {};
      return {
        ...item,
        ...override,
        source: FORM_QUESTION_SOURCES.SHARED,
      };
    });

  const specific = (form?.questions || [])
    .filter((item) => item && !isPersonalInfoLibraryId(item?.questionId))
    .map((item) => ({
      ...item,
      source: item.source || FORM_QUESTION_SOURCES.TRACKER,
    }));

  const sharedGeneral = shared.filter((q) => !isMeasurableQuestion(q));
  const specificGeneral = specific.filter((q) => !isMeasurableQuestion(q));
  const sharedMeasurable = shared.filter((q) => isMeasurableQuestion(q));
  const specificMeasurable = specific.filter((q) => isMeasurableQuestion(q));

  return [
    ...personal,
    ...sharedGeneral,
    ...specificGeneral,
    ...sharedMeasurable,
    ...specificMeasurable,
  ].map((item, index) => ({ ...item, order: index }));
};
